import { useCallback, useEffect, useState } from "react";
import { Sparkles, Globe2, ExternalLink, BookOpen, Loader2, AlertTriangle, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { LumiVoiceButton } from "@/components/lumi/LumiVoiceButton";
import { LumiAvatar } from "@/components/lumi/LumiAvatar";
import { preloadLumiTexts } from "@/lib/audio/lumiAudioCache";
import { lumiPlayback } from "@/lib/audio/lumiPlaybackController";

const LUMI_INTRO = "Let’s explore this idea together. I’ll show you how it connects to real life.";

/** Strip Perplexity-style citation markers like [1], [2][3] from display text. */
function stripCitations(text: string | null | undefined): string {
  if (!text) return "";
  return text.replace(/\s*\[\d+\](?:\s*\[\d+\])*/g, "").replace(/\s{2,}/g, " ").trim();
}

interface ExploreExample {
  title: string;
  summary: string;
  why_it_matters: string;
  reflection_question: string;
  source_title?: string | null;
  source_url?: string | null;
  source_domain?: string | null;
}

interface ExploreResult {
  summary_id?: string;
  lesson_id?: string;
  lesson_name?: string;
  pillar?: "Finance" | "Economics" | "Entrepreneurship" | string;
  concept_summary?: string;
  examples?: ExploreExample[];
  disclaimer?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lessonId: string;
  lessonName: string;
}

type ViewState =
  | { kind: "loading" }
  | { kind: "ready"; data: ExploreResult; cached: boolean }
  | { kind: "empty"; reason?: string }
  | { kind: "error" };

function track(event: string, detail: Record<string, unknown> = {}) {
  // Safe, lesson-scoped only. No private learner data.
  try { console.info("[explore]", event, detail); } catch { /* noop */ }
}

const PILLAR_TONE: Record<string, { instructor: string; accent: string }> = {
  Finance: { instructor: "Aelyra Solari", accent: "from-primary/20 to-accent/10" },
  Economics: { instructor: "Caelion Veyr", accent: "from-secondary/20 to-primary/10" },
  Entrepreneurship: { instructor: "Headmaster Vaelor", accent: "from-accent/20 to-primary/10" },
};

export default function ExploreModal({ open, onOpenChange, lessonId, lessonName }: Props) {
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    track("explore_opened", { lesson_id: lessonId });
    try {
      const { data, error } = await supabase.functions.invoke("perplexity-lesson-explore", {
        body: { lesson_id: lessonId },
      });
      if (error) throw error;
      if (data?.error) {
        setState({ kind: "error" });
        track("explore_failed", { lesson_id: lessonId, reason: data.error });
        return;
      }
      if (!data?.result || !Array.isArray(data.result.examples) || data.result.examples.length === 0) {
        setState({ kind: "empty", reason: data?.reason });
        track("explore_failed", { lesson_id: lessonId, reason: data?.reason ?? "no_results" });
        return;
      }
      const result = { ...(data.result as ExploreResult), summary_id: data.summary_id };
      setState({ kind: "ready", data: result, cached: !!data.cached });
      track("explore_loaded", { lesson_id: lessonId, cached: !!data.cached, count: data.result.examples.length });
    } catch (e) {
      setState({ kind: "error" });
      track("explore_failed", { lesson_id: lessonId, reason: "exception" });
    }
  }, [lessonId]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  // Stop any Lumi playback when the modal closes so audio never leaks
  // across navigation or dialog dismissal.
  useEffect(() => {
    if (!open) lumiPlayback.stop();
    return () => {
      if (open) lumiPlayback.stop();
    };
  }, [open]);

  // Warm the ElevenLabs URL cache for approved/verified lines so the first
  // tap plays Lumi's real voice instead of the browser TTS fallback.
  // - Intro: static approved allowlisted line → allowGenerate.
  // - Concept summary: verified explore-summary path → allowGenerate with summaryId.
  // Example texts are dynamic Perplexity content and are not in the
  // approved-lines manifest, so the server will not generate for them.
  useEffect(() => {
    if (state.kind !== "ready") return;
    void preloadLumiTexts([LUMI_INTRO], lessonId, { allowGenerate: true, mode: "static" });
    if (state.data.concept_summary && state.data.summary_id) {
      void preloadLumiTexts(
        [stripCitations(state.data.concept_summary)],
        lessonId,
        {
          allowGenerate: true,
          mode: "explore-summary",
          summaryId: state.data.summary_id,
        },
      );
    }
  }, [state, lessonId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-card/95 backdrop-blur-xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <LumiAvatar size="sm" />
            <DialogTitle className="font-heading">Explore with Lumi</DialogTitle>
          </div>
          <DialogDescription className="text-xs">
            {lessonName} · curated examples · educational only
          </DialogDescription>
          <div className="mt-2 flex items-start gap-2 rounded-xl border border-primary/20 bg-primary/5 p-2.5">
            <p className="flex-1 text-xs italic leading-relaxed text-foreground/80">“{LUMI_INTRO}”</p>
            <LumiVoiceButton text={LUMI_INTRO} contextId={lessonId} label="Hear Lumi’s intro" allowGenerate />
          </div>
        </DialogHeader>

        {state.kind === "loading" && (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-accent" />
            <p className="text-sm">Searching for safe real-world examples…</p>
          </div>
        )}

        {state.kind === "error" && (
          <ErrorState
            icon={AlertTriangle}
            title="Explore is unavailable right now"
            body="Your quiz progress is still saved. Please try again later."
            onRetry={() => void load()}
          />
        )}

        {state.kind === "empty" && (
          <ErrorState
            icon={BookOpen}
            title="No safe current example yet"
            body="We couldn’t find an age-appropriate real-world example for this lesson right now. Try again later."
            onRetry={() => void load()}
          />
        )}

        {state.kind === "ready" && (
          <ReadyState data={state.data} cached={state.cached} lessonId={lessonId} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ErrorState({
  icon: Icon, title, body, onRetry,
}: { icon: typeof AlertTriangle; title: string; body: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
      <Icon className="h-7 w-7 text-muted-foreground" />
      <div>
        <p className="font-heading font-semibold">{title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry}>Try again</Button>
    </div>
  );
}

function ReadyState({
  data, cached, lessonId,
}: { data: ExploreResult; cached: boolean; lessonId: string }) {
  const pillar = data.pillar ?? "Finance";
  const tone = PILLAR_TONE[pillar] ?? PILLAR_TONE.Finance;

  return (
    <div className="space-y-4">
      <div className={`rounded-2xl border border-border bg-gradient-to-br ${tone.accent} p-4`}>
        <div className="mb-1 flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-accent" />
          <span className="text-[10px] font-heading font-semibold uppercase tracking-widest text-accent">
            {pillar} · guided by {tone.instructor}
          </span>
          {cached && (
            <Badge variant="secondary" className="ml-auto text-[10px]">Saved version</Badge>
          )}
        </div>
        {data.concept_summary && (
          <div className="flex items-start gap-2">
            <p className="flex-1 font-body text-sm leading-relaxed text-foreground/90">{stripCitations(data.concept_summary)}</p>
            <LumiVoiceButton
              text={stripCitations(data.concept_summary)}
              contextId={lessonId}
              label="Hear Lumi read this summary"
              allowGenerate
              mode="explore-summary"
              summaryId={data.summary_id ?? null}
            />
          </div>
        )}
      </div>

      <div className="space-y-3">
        {(data.examples ?? []).map((ex, i) => (
          <article key={i} className="rounded-2xl border border-border bg-background/60 p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h3 className="font-heading text-base font-semibold leading-snug">{stripCitations(ex.title)}</h3>
              <div className="flex flex-wrap gap-1">
                <Badge variant="outline" className="text-[10px]">Real-world example</Badge>
                <Badge variant="outline" className="text-[10px]">Current context</Badge>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-foreground/85">{stripCitations(ex.summary)}</p>
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
              <div className="mb-1 flex items-center gap-2">
                <Brain className="h-3.5 w-3.5 text-primary" />
                <span className="text-[10px] font-heading font-semibold uppercase tracking-widest text-primary">
                  Why it connects
                </span>
              </div>
              <p className="text-sm leading-relaxed text-foreground/85">{stripCitations(ex.why_it_matters)}</p>
            </div>
            <div className="rounded-xl border border-accent/20 bg-accent/5 p-3">
              <div className="mb-1 flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-accent" />
                <span className="text-[10px] font-heading font-semibold uppercase tracking-widest text-accent">
                  Think deeper
                </span>
              </div>
              <p className="text-sm italic leading-relaxed text-foreground/85">{stripCitations(ex.reflection_question)}</p>
            </div>
            {ex.source_url && (
              <a
                href={ex.source_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={() => track("explore_source_clicked", { lesson_id: lessonId, domain: ex.source_domain })}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="h-3 w-3" />
                {ex.source_title || ex.source_domain || "Source"}
                {ex.source_domain && <span className="opacity-60">· {ex.source_domain}</span>}
              </a>
            )}
          </article>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2">
        <Badge variant="outline" className="text-[10px]">Educational only</Badge>
        <p className="text-[11px] text-muted-foreground">
          {data.disclaimer ?? "For financial education only — not financial, legal, tax, or investment advice."}
        </p>
      </div>
    </div>
  );
}